/**
 * packages/ui/react/JurisdictionPicker.jsx — THE jurisdiction picker. One copy, three mounts.
 *
 * ══ 🔴 RUN RK — CONSOLIDATED, ON ADAM'S RULING. ═════════════════════════════════════════════════
 * There were two components — `apps/main/.../Clark/JurisdictionPicker.jsx` (242 lines) and
 * `apps/steel/.../components/JurisdictionPicker.jsx` (359) — with NO import between them. Clark's
 * own header said it: "COPIED, NEVER IMPORTED … the cost is that a change here must be made there
 * too." That cost came due three runs running (RUN PK's home-state link, RUN RJ's three touch
 * properties and the scrim z-index). This file is the fix for the cause rather than the symptom.
 *
 * 🔴 THE MERGE IS STEEL'S FILE PLUS CLARK'S FOUR DIFFERENCES, because Steel's was the superset:
 * it alone had the `anchorRef` placement maths (RUN RH/SF) and the real `homeState` preset. What
 * Clark had and Steel did not, all four now here for everyone:
 *   1. CONTROLLED, NOT CONTEXT-BOUND. Steel read `AppContext.jurisSel` directly, which is why this
 *      could never be shared — apps/main has no such context. It now takes `value`/`onChange` with
 *      the IDENTICAL `{sel, preset}` shape, and Steel's chip passes its context in. The data model
 *      is unchanged; only its owner is.
 *   2. AN ESCAPE HANDLER. A `role="dialog"` a keyboard cannot dismiss is a trap. Clark had one and
 *      Steel's picker did not (Steel's CHIP carried its own; both close, so the duplicate is inert).
 *   3. `type="button"` ON EVERY BUTTON. Steel omitted it — harmless where it is, fatal the day this
 *      renders inside a <form>.
 *   4. `data-testid` HOOKS, passed in by name so neither app's probes break.
 *
 * ⚠️ `homeState` IS A PROP AND MUST STAY ONE. Research's is LIVE (`Settings.jsx`, `steel.homeState`,
 * Matters P4B) and Clark's has no field yet, so Clark passes nothing and the "My state" preset is
 * simply absent there. RUN PK established that Research's must not be removed to make the two
 * match; making it a prop is how both stay true at once.
 *
 * Renders the CANONICAL Indigo Book T1/T3 tree, unchanged.
 *
 * Renders the CANONICAL Indigo Book T1/T3 tree (@citeclerk/goodlaw jurisdictions) — Federal column
 * (U.S. Supreme Court · 13 Courts of Appeals → districts · specialty courts) + a compact state/territory grid
 * (check = whole jurisdiction · expand for T1 court levels). Presets, search, cascading multi-select, removable
 * selection pills. Pops UP from the pill's jurisdiction button, anchored with a pointer, all visible without
 * scrolling. Ultramarine + premium, built on Steel's --cc-* tokens (design language from the approved mockup).
 *
 * Selection is committed on Apply → AppContext.jurisSel ({sel:[{id,name,abbr,kind}], preset}); Phase 3 maps it
 * to the answer scope filter. Nothing here touches good-law determination.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FEDERAL, STATES, TERRITORIES } from '@citeclerk/goodlaw/jurisdictions.js';
import './jurispicker.css';

// Flat id→entry lookup. `label` = the SHORT one-line picker label; `name` = full canonical (tooltip + honesty
// check). States/territories label = the Indigo Book T12 geographical abbreviation (the `abbr` field); federal
// courts label = an explicit displayLabel where set, else the full name; districts/circuits keep their name.
export const ENTRY = (() => {
  const m = {};
  const sup = FEDERAL.supreme;
  m[sup.id] = { id: sup.id, name: sup.name, label: sup.name, abbr: sup.abbr, kind: 'court' };
  for (const c of FEDERAL.circuits) {
    m[c.id] = { id: c.id, name: c.name, label: c.name, abbr: c.abbr, kind: 'circuit' };
    for (const d of c.districts) m[d.id] = { id: d.id, name: d.name, label: d.name, abbr: d.abbr, kind: 'district' };
  }
  for (const s of FEDERAL.specialty) m[s.id] = { id: s.id, name: s.name, label: s.displayLabel || s.name, abbr: s.abbr, kind: 'court' };
  for (const s of [...STATES, ...TERRITORIES]) {
    m[s.id] = { id: s.id, name: s.name, label: s.displayLabel || s.abbr, abbr: s.abbr, kind: 'jurisdiction' };
    for (const c of s.courts) m[c.id] = { id: c.id, name: `${s.abbr} ${c.name}`, label: `${s.abbr} ${c.name}`, abbr: c.abbr, kind: 'court' };
  }
  return m;
})();

const FED_IDS = [FEDERAL.supreme.id, ...FEDERAL.circuits.map((c) => c.id)];

/* ══ 🔴 RUN RH · RH-2 — THE SHEET CAN NOW BE ANCHORED TO SOMETHING THAT IS NOT THE PILL. ═══════
   This picker was written to pop UP from the floating pill: `position: fixed; bottom: 84px` with a
   pointer on its lower edge aimed at the pill's jurisdiction button (jurispicker.css:5-22). RH-2
   moves that button off the pill and onto the search composer, and a sheet still pinned 84px above
   the viewport floor would sit at the bottom of the screen with a pointer aimed at a control that
   is no longer there — the arrow would be pointing at `Shortcuts`.

   `anchorRef` is ADDITIVE and OPTIONAL. With it absent this component computes nothing and renders
   exactly what it rendered before, so any caller still popping it from the pill is untouched.
   ⭐ It measures at OPEN, not at render, and re-measures on resize/scroll — the sheet is up to
   620px tall and the composer sits mid-page, so which side it can open on is a fact about the
   viewport, not a constant. It takes the side with more room and clamps its height to that room,
   which is what stops it clipping its own Apply button off the bottom of a short laptop screen. */
export default function JurisdictionPicker({
  open,
  onClose,
  value,
  onChange,
  /* Research passes its live home state; Clark passes nothing, so the preset is absent there rather
     than rendering a control that selects nothing. See the header. */
  homeState = null,
  anchorRef = null,
  testId,
  applyTestId,
}) {
  /* The committed selection, normalised. A caller that has not initialised yet gets the same empty
     shape the context used to hand out, so first paint is identical for both apps. */
  const jurisSel = value && Array.isArray(value.sel) ? value : { sel: [], preset: 'all' };
  const setJurisSel = onChange;
  const homeEntry = homeState ? ENTRY[homeState] : null;
  const [checked, setChecked] = useState(() => new Set(jurisSel.sel.map((e) => e.id)));
  const [preset, setPreset] = useState(jurisSel.preset || 'all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [q, setQ] = useState('');
  const searchRef = useRef(null);

  // re-seed from committed selection each time it opens (so Cancel/close-without-apply reverts cleanly)
  useEffect(() => {
    if (open) {
      setChecked(new Set(jurisSel.sel.map((e) => e.id)));
      setPreset(jurisSel.preset || (jurisSel.sel.length ? null : 'all'));
      setQ('');
      setTimeout(() => searchRef.current?.focus(), 60);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* 🔴 RUN RK — ESCAPE CLOSES IT. From Clark's copy; Steel's picker never had one (its CHIP did, so
     Research is not gaining behaviour, it is gaining it in the right place). `stopPropagation` so a
     dialog nested inside another surface does not dismiss both. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const toggle = (id) => {
    setPreset(null);
    setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const applyPreset = (kind) => {
    if (kind === 'mystate' && !homeState) return; // no home state set → the button just prompts, selects nothing
    setPreset(kind);
    if (kind === 'all') setChecked(new Set());
    else if (kind === 'fed') setChecked(new Set(FED_IDS));
    else if (kind === 'mystate') setChecked(new Set([homeState]));
  };
  const clearAll = () => { setChecked(new Set()); setPreset('all'); };
  const commit = () => {
    const sel = [...checked].map((id) => ENTRY[id]).filter(Boolean);
    setJurisSel({ sel, preset: sel.length ? (preset === 'all' ? null : preset) : 'all' });
    onClose();
  };

  /* 🔴 RUN RH · RH-2 — placement, only when anchored. `null` means "the CSS's own rule stands". */
  const [place, setPlace] = useState(null);
  const sheetRef = useRef(null);
  useEffect(() => {
    if (!open || !anchorRef?.current) { setPlace(null); return undefined; }
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      /* 🔴 RUN VF — BELOW 720px THE CSS'S OWN FULL-SHEET RULE GOVERNS, AND THE ANCHOR MATH MUST NOT
         FIGHT IT. jurispicker.css's `@media (max-width:720px)` pins all four edges (`top:72px;
         bottom:10px;left:10px;right:10px`) so the sheet can never run off-screen regardless of
         content height (STREAM C · 7.4's fix for the panel opening above the fold). This effect
         writes `top`/`bottom`/`maxHeight` as INLINE styles, which beat that rule outright, while
         `left`/`right` stay CSS-only — so the anchor math was landing an inline `left` (centred on
         a chip near the bottom of the screen) against the CSS's `right: 10px`, and `width: auto`
         between a JS pixel and a CSS pixel computed to ~115px: a sliver, not a sheet. Measured on
         the live picker at 390×844: `.jp` rendered 115px wide, the search input (172px, refusing to
         shrink below its intrinsic min-width) ran to x=448.6 off a 390px viewport, `.jp__preset
         --prompt`'s text wrapped into a ~68px column ("Se…" / "you…" / "hon…" / "state" / "setti…"),
         and `.jp__cols` needed 1112px of scrollable height against 536px given. Bowing out here —
         same call `!open`/`!anchorRef.current` above already make — leaves `place` null, which the
         second effect below already treats as "clear the inline overrides", and the CSS rule is the
         only author of the box. */
      if (window.matchMedia('(max-width: 720px)').matches) { setPlace(null); return; }
      const r = el.getBoundingClientRect();
      const GAP = 12;                       // the pointer is 14px on the diagonal; 12 clears it
      /* ⚠️ NO TOP-BAR CLAMP, AND THE REASON IS A FINDING RATHER THAN A CHOICE. The first cut of
         this reserved the header's height with `document.querySelector('.st-topbar')` — and
         `.st-topbar` IS RENDERED NOWHERE. It exists only as rules in chrome.css; Steel's header is
         `@citeclerk/ui/Header` (`.cc-marketing-nav-*`), which this page has used since the vendored
         bar arrived. The clamp measured 0 and changed nothing, and it would have sat here reading
         like a solved problem. ⭐ A selector that matches nothing does not throw — `?.` and a
         `|| 0` swallow it — which is the same shape as the `getElementById('wk-search')` binding
         this run deleted one file over. Reported, not papered over.
         It is also not NEEDED: that header is static, not fixed, so it scrolls with the page and a
         dialog over it costs no navigation. The sheet is fitted to the viewport instead. */
      const above = r.top - GAP;
      const below = window.innerHeight - r.bottom - GAP;
      /* The side with more room wins, and the sheet is capped to THAT room rather than to a
         constant. A sheet taller than the space it opened into scrolls its own body; a sheet that
         ignores the space is one whose Apply button is off-screen. */
      const openUp = above >= below;
      const room = Math.max(160, (openUp ? above : below) - 8);
      /* ══ 🔴 RUN SF · SF-1 — THE HORIZONTAL HALF, WHICH RH COULD NOT HAVE EXERCISED. ═════════════
         RH's measurement above resolves which SIDE the sheet opens on and how tall it may be. It
         never computes an X, and the reason it never had to is that its only anchored caller —
         Landing's chip — is itself centred under a centred composer, so `left: 50%` happened to be
         the right answer and a missing rule and a correct one are indistinguishable.

         SF-1 mounts the same chip in `.wk-qzone__q`, where it sits at the RIGHT end of a 1400px
         row. Measured before this line existed: the chip's centre at 1440x900 is x≈1246 and the
         pointer stayed at x=719 — half a screen away, aimed at the question text. RH's own warning,
         in the form it actually occurs.

         🔴 THE SHEET SLIDES AND THE POINTER FOLLOWS IT, AND THE FIRST CUT OF THIS DID ONLY THE
         SECOND. Moving the pointer alone LOOKS sufficient — until the anchor is outside the sheet
         altogether, which is the actual case here. MEASURED with the pointer-only version in place:
         sheet 280→1160, chip centre 1237, pointer clamped to its own right corner at x=1134 and
         **103px short of the chip**. A pointer 103px from the thing it claims to point at is the
         same defect as one 519px away, quieter. So the sheet is centred ON THE ANCHOR and clamped
         to the viewport, and the pointer is then resolved against wherever that put it.

         ⭐ AND THIS IS WHY IT IS A NO-OP ON THE PILL-ANCHORED AND LANDING PATHS RATHER THAN A
         SECOND BEHAVIOUR: "centred on the anchor" applied to a CENTRED anchor is "centred". The
         zero state's chip is at x=720 of 1440, so the clamp is not reached and the sheet lands
         exactly where `left: 50%` put it. This run measures that instead of claiming it — 19
         themes, both arms, ops/run-sf/out/anchor-*.json.

         ⚠️ THE CLAMP IS THE VIEWPORT, WITH A MARGIN. A sheet centred on a chip near the right edge
         would hang off it; 16px is the margin, and `Math.max` before `Math.min` means a viewport
         narrower than the sheet degrades to "flush left" rather than to an inverted range. */
      const RADIUS = 26;
      const EDGE = 16;
      const anchorCx = r.left + r.width / 2;
      setPlace(Object.assign(
        openUp
          ? { bottom: Math.round(window.innerHeight - r.top + GAP), top: 'auto', maxHeight: Math.round(room), dir: 'up' }
          : { top: Math.round(r.bottom + GAP), bottom: 'auto', maxHeight: Math.round(room), dir: 'down' },
        { anchorCx, radius: RADIUS, edge: EDGE },
      ));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, anchorRef]);

  /* 🔴 RUN SF · SF-1 — THE POINTER'S X, WRITTEN AFTER LAYOUT. ⭐ AND CLEARED WHEN UNANCHORED, so
     the pill's caller keeps `left: 50%` exactly as the stylesheet declares it: a stale custom
     property on a shared node is how one caller's fix becomes another caller's defect. */
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return undefined;
    if (!place) { el.style.removeProperty('--jp-arrow-x'); el.style.removeProperty('left'); return undefined; }
    const write = () => {
      const node = sheetRef.current;
      if (!node) return;
      /* The WIDTH is measured; the LEFT is computed. Reading the left back after writing it would
         be reading this effect's own output, and the sheet is `translateX(-50%)`, so the number
         that matters is the centre and not the edge. */
      const w = node.getBoundingClientRect().width;
      const vw = window.innerWidth;
      const halfW = w / 2;
      const lo = Math.min(vw / 2, halfW + place.edge);
      const hi = Math.max(lo, vw - halfW - place.edge);
      const centre = Math.min(hi, Math.max(lo, place.anchorCx));
      node.style.left = `${Math.round(centre)}px`;
      // the pointer, in the sheet's own coordinates, against the edge that centre implies
      const aLo = place.radius;
      const aHi = Math.max(aLo, w - place.radius);
      const ax = Math.min(aHi, Math.max(aLo, place.anchorCx - (centre - halfW)));
      node.style.setProperty('--jp-arrow-x', `${Math.round(ax)}px`);
    };
    write();
    window.addEventListener('resize', write);
    return () => window.removeEventListener('resize', write);
  }, [place]);

  const ql = q.trim().toLowerCase();
  const matches = (text) => !ql || String(text).toLowerCase().includes(ql);
  const selEntries = useMemo(() => [...checked].map((id) => ENTRY[id]).filter(Boolean), [checked]);

  if (!open) return null;
  const toggleExp = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isExp = (id) => expanded.has(id) || !!ql; // search auto-expands

  return (
    <div
      className="jp"
      role="dialog"
      aria-label="Choose jurisdictions"
      /* `data-anchored` is what jurispicker.css keys the pointer's side and the z-index off. The
         pill's own copy renders inside the pill's stacking context, where `z-index: 60` is above
         everything local; anchored to the composer it is a sibling of the page and has to beat the
         floating pill's 400 outright, or the sheet opens BEHIND the thing it replaced. */
      data-anchored={place ? place.dir : undefined}
      data-testid={testId}
      ref={sheetRef}
      /* 🔴 RUN SF — `--jp-arrow-x` IS RESOLVED AGAINST THE SHEET, NOT THE VIEWPORT, because that is
         what `::after { left: … }` is resolved against. It is written in an effect rather than here
         for one reason: the sheet's own left edge is not known until it has been laid out, and a
         value computed from an assumed width is a number that is right at 1440 and wrong at 900. */
      style={place ? { top: place.top, bottom: place.bottom, maxHeight: place.maxHeight } : undefined}
    >
      <div className="jp__head">
        {/* 🔴 RUN CF · BLOCK 1 — PLURAL, TO AGREE WITH THE CONTROL THAT OPENS IT. The sheet is a
            multi-select (its own footer counts "N selected") and the button now reads
            "Jurisdictions"; a singular title over a plural control is the kind of one-word
            disagreement `lib/jurisLabel.js` records this codebase already paying for once, when
            Research said "Jurisdiction: Mississippi" and Clark said "Mississippi". */}
        <h3 className="jp__title">Jurisdictions</h3>
        <input ref={searchRef} className="jp__search" placeholder="Search courts or states…"
          value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search courts or states" />
      </div>

      <div className="jp__presets">
        <button type="button" className={`jp__preset ${preset === 'all' ? 'is-on' : ''}`} onClick={() => applyPreset('all')}>All jurisdictions</button>
        <button type="button" className={`jp__preset ${preset === 'fed' ? 'is-on' : ''}`} onClick={() => applyPreset('fed')}>Federal only</button>
        {/* 🔴 RUN PK — "Set your home state in settings →" IS REMOVED HERE TOO (Amendment 3 §2
            said BOTH platforms; RUN CK removed Clark's copy only, and this one stayed live at
            /research — the "second render path" of the RUN PK audit).
            ⚠️ UNLIKE CLARK'S, THIS PROMPT POINTED AT A REAL SETTING: pages/Settings.jsx has a
            working home-state select (steel.homeState, Matters P4B), so the orphan check that
            justified Clark's removal does NOT transfer. The ruling still stands — the disabled
            dead-end button goes — but only the prompt arm is removed. The `My state` preset below
            is REACHABLE here the moment a home state is set, and it stays. */}
        {homeState
          ? <button type="button" className={`jp__preset ${preset === 'mystate' ? 'is-on' : ''}`} onClick={() => applyPreset('mystate')}>My state ({homeEntry?.label || homeEntry?.abbr})</button>
          : null}
      </div>

      {selEntries.length > 0 && (
        <div className="jp__pills">
          {selEntries.slice(0, 10).map((e) => (
            <button key={e.id} className="jp__pill" onClick={() => toggle(e.id)} title="Remove">
              {e.abbr || e.name} <span className="jp__pill-x" aria-hidden="true">✕</span>
            </button>
          ))}
          {selEntries.length > 10 && <span className="jp__more">+{selEntries.length - 10} more</span>}
        </div>
      )}

      <div className="jp__cols">
        {/* Federal column */}
        <div className="jp__col jp__col--fed">
          <h4 className="jp__eyebrow">Federal</h4>
          <Row entry={ENTRY[FEDERAL.supreme.id]} checked={checked} toggle={toggle} show={matches(FEDERAL.supreme.name)} />
          {FEDERAL.circuits.map((c) => {
            const kids = c.districts;
            const show = matches(c.name) || kids.some((d) => matches(d.name));
            if (!show) return null;
            return (
              <div key={c.id}>
                <Row entry={ENTRY[c.id]} checked={checked} toggle={toggle} show
                  expandable={kids.length > 0} expanded={isExp(c.id)} onExpand={() => toggleExp(c.id)} />
                {kids.length > 0 && isExp(c.id) && (
                  <div className="jp__sub">
                    {kids.filter((d) => matches(d.name)).map((d) => (
                      <Row key={d.id} entry={ENTRY[d.id]} checked={checked} toggle={toggle} show small />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <h4 className="jp__eyebrow jp__eyebrow--gap">Specialty courts</h4>
          {FEDERAL.specialty.filter((s) => matches(s.name)).map((s) => (
            <Row key={s.id} entry={ENTRY[s.id]} checked={checked} toggle={toggle} show />
          ))}
        </div>

        {/* State + territory grid */}
        <div className="jp__col jp__col--states">
          <h4 className="jp__eyebrow">State &amp; territory — check = whole jurisdiction · ▾ = court levels</h4>
          <div className="jp__grid">
            {[...STATES, ...TERRITORIES].map((s) => {
              const show = matches(s.name) || s.courts.some((c) => matches(c.name));
              if (!show) return null;
              return (
                <div key={s.id} className="jp__gcell">
                  <Row entry={ENTRY[s.id]} checked={checked} toggle={toggle} show
                    expandable expanded={isExp(s.id)} onExpand={() => toggleExp(s.id)} />
                  {isExp(s.id) && (
                    <div className="jp__sub">
                      {s.courts.filter((c) => matches(c.name)).map((c) => (
                        <label key={c.id} className="jp__row jp__row--small">
                          <input type="checkbox" checked={checked.has(c.id)} onChange={() => toggle(c.id)} />
                          <span title={c.note || undefined}>{c.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="jp__foot">
        <span className="jp__count">
          {selEntries.length ? `${selEntries.length} selected` : 'All jurisdictions'}
        </span>
        <div className="jp__actions">
          <button type="button" className="jp__clear" onClick={clearAll}>Clear</button>
          <button type="button" className="jp__apply" onClick={commit} data-testid={applyTestId}>Apply</button>
        </div>
      </div>
    </div>
  );
}

function Row({ entry, checked, toggle, show, expandable, expanded, onExpand, small }) {
  if (!entry || show === false) return null;
  return (
    <div className={`jp__row ${small ? 'jp__row--small' : ''}`}>
      <input type="checkbox" checked={checked.has(entry.id)} onChange={() => toggle(entry.id)} />
      <span className="jp__rowname" title={entry.name} onClick={() => toggle(entry.id)}>{entry.label || entry.name}</span>
      {expandable && (
        <button type="button" className="jp__exp" onClick={onExpand} aria-label={expanded ? 'Collapse' : 'Expand'}>{expanded ? '▾' : '▸'}</button>
      )}
    </div>
  );
}
